// Wave 13 / Track B finish — Software z-buffer region occlusion.
//
// Complements regionVisibility.ts (which uses MediaPipe landmark
// confidence as a proxy for occlusion). This module does the *actual*
// occlusion test the plan called for: rasterize the cylinder/sphere
// primitives from the wind direction onto a small offscreen buffer,
// keep the closest region id per pixel, then histogram.
//
// Pure JS — no WebGL plumbing. The body has at most 10 primitives,
// rasterizing them at 128x128 takes ~5 ms even unoptimized. Runs at
// 5 Hz from useVisionPipeline alongside the landmark-visibility sampler.
//
// Output is in [0,1] per region: fraction of total visible-rider pixels
// occupied by each region. Two different rider poses with the same
// silhouette area but different limb arrangements give different
// per-region pcts here, which is exactly what we want.

import * as THREE from 'three';
import { BodyPartFrame, BodyRegion } from './cylinderBody';

const BUFFER_SIZE = 128; // 128x128 raster

export interface RegionOcclusion {
  head: number;
  torso: number;
  arms: number;
  legs: number;
  /** Total occupied pixels in the buffer — useful as a sanity gate. */
  total: number;
}

/** Rasterize the body primitives from the wind direction and histogram
 *  the per-region pixel counts after z-buffer occlusion. Returns null
 *  when no primitives are visible (e.g. all parts have visibility < 0.4). */
export function computeRegionOcclusion(
  parts: BodyPartFrame[],
  windWorld: THREE.Vector3,
): RegionOcclusion | null {
  if (!parts || parts.length === 0) return null;

  // Build a camera frame from the wind direction. The wind direction is
  // the +Z of the camera (the camera looks BACK along the wind, so the
  // rider is in front). Pick any orthogonal X, then Y = Z×X.
  const camZ = windWorld.clone().normalize();
  const camY = Math.abs(camZ.y) < 0.9
    ? new THREE.Vector3(0, 1, 0)
    : new THREE.Vector3(1, 0, 0);
  const camX = new THREE.Vector3().crossVectors(camY, camZ).normalize();
  const camYTrue = new THREE.Vector3().crossVectors(camZ, camX).normalize();

  // First pass — find the rider's world-space bbox in the camera frame so
  // we can fit the raster tight to the body without wasting buffer space.
  let xMin = Infinity, xMax = -Infinity, yMin = Infinity, yMax = -Infinity;
  for (const part of parts) {
    if (part.visibility < 0.3) continue;
    const halfLen = part.kind === 'sphere' ? part.radius : part.length / 2;
    // 6 sample points: ends + 4 corners of the bounding box. Cheap
    // approximation, fine for sizing the raster.
    const samples = [
      part.center.clone().addScaledVector(part.axis, halfLen),
      part.center.clone().addScaledVector(part.axis, -halfLen),
      part.center.clone().addScaledVector(part.axis, halfLen).add(camX.clone().multiplyScalar(part.radius)),
      part.center.clone().addScaledVector(part.axis, halfLen).add(camX.clone().multiplyScalar(-part.radius)),
      part.center.clone().addScaledVector(part.axis, -halfLen).add(camX.clone().multiplyScalar(part.radius)),
      part.center.clone().addScaledVector(part.axis, -halfLen).add(camX.clone().multiplyScalar(-part.radius)),
    ];
    for (const s of samples) {
      const px = s.dot(camX);
      const py = s.dot(camYTrue);
      if (px < xMin) xMin = px;
      if (px > xMax) xMax = px;
      if (py < yMin) yMin = py;
      if (py > yMax) yMax = py;
    }
  }
  if (!isFinite(xMin) || !isFinite(yMax)) return null;

  // Pad the bbox by 5% so primitives near the edge don't get clipped by
  // sub-pixel rounding.
  const padX = (xMax - xMin) * 0.05;
  const padY = (yMax - yMin) * 0.05;
  xMin -= padX; xMax += padX; yMin -= padY; yMax += padY;
  const worldW = xMax - xMin;
  const worldH = yMax - yMin;
  if (worldW <= 0 || worldH <= 0) return null;

  // Project world point → buffer pixel (sx, sy) + depth.
  const project = (p: THREE.Vector3): { sx: number; sy: number; depth: number } => {
    const px = p.dot(camX);
    const py = p.dot(camYTrue);
    const pz = p.dot(camZ);
    // Buffer y axis grows downward (image convention).
    const sx = ((px - xMin) / worldW) * (BUFFER_SIZE - 1);
    const sy = (1 - (py - yMin) / worldH) * (BUFFER_SIZE - 1);
    return { sx, sy, depth: pz };
  };

  // Z-buffer + region id per pixel. depth +inf == no draw yet.
  const zBuf = new Float32Array(BUFFER_SIZE * BUFFER_SIZE);
  zBuf.fill(Infinity);
  // region id: 0 empty, 1 head, 2 torso, 3 arms, 4 legs (matches the
  // segmentation label convention).
  const regionBuf = new Uint8Array(BUFFER_SIZE * BUFFER_SIZE);

  const regionId = (r: BodyRegion): number =>
    r === 'head' ? 1 : r === 'torso' ? 2 : r === 'arms' ? 3 : 4;

  // Rasterize each part as a thick line segment (cylinder) or filled
  // disk (sphere). For cylinders we walk the axis at a step of half the
  // radius and stamp circular footprints; this is the simplest robust
  // approach for rounded primitives at this resolution.
  const stampDisk = (cx: number, cy: number, depth: number, rPx: number, region: number) => {
    const r2 = rPx * rPx;
    const x0 = Math.max(0, Math.floor(cx - rPx));
    const x1 = Math.min(BUFFER_SIZE - 1, Math.ceil(cx + rPx));
    const y0 = Math.max(0, Math.floor(cy - rPx));
    const y1 = Math.min(BUFFER_SIZE - 1, Math.ceil(cy + rPx));
    for (let y = y0; y <= y1; y++) {
      const dy = y - cy;
      const dy2 = dy * dy;
      for (let x = x0; x <= x1; x++) {
        const dx = x - cx;
        if (dx * dx + dy2 > r2) continue;
        const idx = y * BUFFER_SIZE + x;
        if (depth < zBuf[idx]) {
          zBuf[idx] = depth;
          regionBuf[idx] = region;
        }
      }
    }
  };

  // Convert world radius → buffer pixels. Use the average of x/y scales.
  const pxPerWorld = ((BUFFER_SIZE - 1) / worldW + (BUFFER_SIZE - 1) / worldH) * 0.5;

  for (const part of parts) {
    if (part.visibility < 0.3) continue;
    const rid = regionId(part.region as BodyRegion);
    const rPx = Math.max(1.5, part.radius * pxPerWorld);
    if (part.kind === 'sphere') {
      const c = project(part.center);
      stampDisk(c.sx, c.sy, c.depth, rPx, rid);
    } else {
      // Cylinder: walk the axis in world space at half-radius steps,
      // stamp a disk at each step. Depth = projection of the step
      // center along camZ — the z-buffer handles ordering correctly
      // across overlapping primitives.
      const halfLen = part.length / 2;
      const stepWorld = Math.max(part.radius * 0.5, 0.01);
      const nSteps = Math.max(2, Math.ceil(part.length / stepWorld));
      for (let i = 0; i <= nSteps; i++) {
        const t = -halfLen + (i / nSteps) * part.length;
        const wp = part.center.clone().addScaledVector(part.axis, t);
        const p = project(wp);
        stampDisk(p.sx, p.sy, p.depth, rPx, rid);
      }
    }
  }

  let head = 0, torso = 0, arms = 0, legs = 0, total = 0;
  for (let i = 0; i < regionBuf.length; i++) {
    const r = regionBuf[i];
    if (r === 0) continue;
    total++;
    if (r === 1) head++;
    else if (r === 2) torso++;
    else if (r === 3) arms++;
    else if (r === 4) legs++;
  }
  if (total === 0) return null;
  return {
    head: head / total,
    torso: torso / total,
    arms: arms / total,
    legs: legs / total,
    total,
  };
}
